// ── 360° panoráma: tiszta segédfüggvények (WebGL nélkül tesztelhetők) ────────

export const MIN_FOV = 30;    // legszűkebb látószög (legnagyobb nagyítás)
export const MAX_FOV = 100;   // legtágabb látószög
export const DEFAULT_FOV = 75;

/** Érték korlátozása [min, max] közé. */
export const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

/** A függőleges nézés korlátozása: ±85°, hogy a pólusnál ne forduljon át a kép. */
export const clampPitch = (deg) => clamp(deg, -85, 85);

/** A vízszintes körbefordulás 0–360 között marad (nem nő végtelenbe). */
export const wrapYaw = (deg) => ((deg % 360) + 360) % 360;

/** Zoom: a látószög szűkítése/tágítása, korlátok között. */
export const applyZoom = (fov, delta) => clamp(fov + delta, MIN_FOV, MAX_FOV);

/** Húzás → elfordulás. A `fov` skálázza: nagyításnál finomabb a mozgás,
 *  különben zoomolva „ugrana” a kép. */
export const dragToRotation = (dx, dy, fov, viewportWidth) => {
  const scale = (fov / DEFAULT_FOV) * (600 / Math.max(320, viewportWidth || 600));
  return { yaw: -dx * 0.25 * scale, pitch: dy * 0.25 * scale };
};

/** Két érintés távolsága (pinch zoomhoz). */
export const touchDistance = (a, b) => {
  if (!a || !b) return 0;
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
};

/** A GPU textúra-korlátjához igazított méret. A nagy Insta360 export (pl.
 *  11968×5984) különben NEM töltődne be – inkább arányosan kicsinyítjük. */
export const fitToMaxTexture = (width, height, maxSize) => {
  if (!width || !height || !maxSize || (width <= maxSize && height <= maxSize)) {
    return { width, height, resized: false };
  }
  const ratio = Math.min(maxSize / width, maxSize / height);
  // round + felső korlát: a floor lebegőpontos hiba miatt 1 pixellel alálőne
  // (4096 helyett 4095), a korlát viszont garantálja, hogy sosem lépjük túl.
  return {
    width: Math.min(maxSize, Math.max(1, Math.round(width * ratio))),
    height: Math.min(maxSize, Math.max(1, Math.round(height * ratio))),
    resized: true,
  };
};

/** Kezdő látószög a canvas képarányához igazítva.
 *
 *  A shader a FÜGGŐLEGES látószöggel dolgozik. Álló (portré) telefonon a
 *  vízszintes látószög ebből nagyon kicsi lesz (390×844-nél ~39°), ezért a
 *  panoráma „bezoomoltnak” tűnne. Keskeny képnél tágítunk, fekvőnél marad a
 *  megszokott 75°.
 */
export const initialFov = (width, height) => {
  if (!width || !height) return DEFAULT_FOV;
  const aspect = width / height;
  const widen = 1 + (1 - Math.min(1, aspect)) * 0.6;
  return clamp(DEFAULT_FOV * widen, MIN_FOV, MAX_FOV);
};

/** Van-e használható WebGL kontextus (fallback-döntéshez). */
export const isWebGLAvailable = () => {
  try {
    const canvas = document.createElement('canvas');
    return Boolean(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
  } catch {
    return false;
  }
};
